// jobStatusActions — starting, pausing and completing a job, declared once.
//
// docs/REBUILD_PARITY.md, Jobs: "Status: start, pause, complete | Jobs,
// TimeTracker | Field | 🟡". Field had no status control at all — Today and
// Job offer the clock and nothing else, and actions.js has no status write —
// so a crew member could clock in on a job and never mark it done. The classic
// screen that holds this is Jobs.js: `activateJob` (one-tap start, :657), the
// pause modal with a REQUIRED reason (:638, :755) and the complete modal with
// an optional note (:644, :767).
//
// ── The contract, read off server/routes/jobs.js ────────────────────────────
//
// PUT /api/jobs/:id  (jobs.js:477) is the endpoint, and it is the one classic
// uses. It is the only writer of `jobs.status` that also takes `status_note`:
//
//   validators  body('status').optional().isIn(sched.JOB_STATUSES)   (:481)
//               body('status_note').optional().trim()                (:484)
//   gate        job.user_id !== req.user.id && role !== 'admin'
//               && role !== 'manager'  →  403 'Access denied'        (:511)
//   statuses    JOB_STATUSES = todo | active | paused | completed |
//               archived   (server/utils/scheduling.js:12)
//   completing  unmet prerequisites → 400 naming them                (:530)
//               stamps completed_at / completed_by, clears a block   (:547)
//   pausing     the note is written into the activity trail as
//               "Job paused - Reason: …"                             (:556)
//   starting    'active' clears completed_at / completed_by          (:558)
//   partial     only fields that are `!== undefined` are written, so
//               { status, status_note } leaves jobs.user_id — the LEAD
//               pointer payroll reads — completely untouched.
//
// PATCH /api/jobs/:id/schedule (:890) also writes status, with the same
// effective permission (sched.canUserReschedule, scheduling.js:73) but NO
// status_note. Dispatch's move sheet uses it; Field does not, because losing
// the pause reason is exactly the regression that was just fixed for clock-out.
//
// ── Who may do it ───────────────────────────────────────────────────────────
//
// Both writers agree: admin, manager, or the job's LEAD (`jobs.user_id`). A
// crew member on the ROSTER (`job_assignees`) and not the lead is refused,
// and GET /api/field/today returns both kinds — lead OR roster (field.js
// workerJobs) — so a Field screen that offered the buttons to everyone on the
// list would answer 403 to a large part of the crew. `canChangeJobStatus`
// mirrors the gate so the buttons are not offered, and `whyStatusRefused`
// says so in a sentence rather than leaving a blank where a button was.
//
// ── Why this does NOT go through the offline queue ──────────────────────────
//
// Every other Field write queues (apps/field/actions.js). This one cannot.
// POST /api/field/sync replays ops through `applyOp`, whose switch accepts
// exactly clock_in | clock_out | subtask | note | problem and answers
// 400 `Unknown op "…"` to anything else (server/routes/field.js, the
// `default:` arm). There is no status op, so a queued status change would be
// accepted by the phone, sit in the queue, and then be dropped with an error
// banner hours later — the crew member believing all along that the job was
// marked done.
//
// So a status change is a live request that refuses when there is no signal,
// the same decision and for the same reason as `claimJob`: what the phone
// cannot promise, it must not appear to promise. QUEUES_OFFLINE records that
// as a value the test can check against the server's op list.

/** Statuses the server accepts (server/utils/scheduling.js JOB_STATUSES). */
export const JOB_STATUSES = ['todo', 'active', 'paused', 'completed', 'archived'];

/** The endpoint. PUT, because it is the only status writer taking a note. */
export const jobStatusPath = (id) => `/jobs/${id}`;

/** False, and checked against applyOp's op list. See the header. */
export const QUEUES_OFFLINE = false;

/**
 * The three moves a crew phone offers, and nothing else.
 *
 * `archived` is deliberately absent: it is a filing decision the office makes
 * (Office Jobs has it) and it takes work off every list a crew reads. `todo`
 * is absent for the same reason — moving work backwards out of `active` is a
 * dispatch decision, and Pause is what a crew member actually means.
 *
 * `note`:
 *   'required'  the server records it and dispatch reads it; a pause with no
 *               reason is the thing the classic screen refuses (Jobs.js:756)
 *   'optional'  offered, and sent only when it has something in it
 */
export const JOB_STATUS_ACTIONS = [
  {
    key: 'start',
    label: 'Start work',
    to: 'active',
    from: ['todo', 'paused'],
    note: 'none',
    variant: 'go',
    icon: 'play',
    title: 'Start work',
  },
  {
    key: 'pause',
    label: 'Pause',
    to: 'paused',
    from: ['todo', 'active'],
    note: 'required',
    variant: 'ghost',
    icon: 'pause',
    title: 'Why are you pausing this job?',
    hint: 'The office reads this. Waiting on a part, gate locked, weather — whatever it is.',
    placeholder: 'Waiting on the stone delivery',
  },
  {
    key: 'complete',
    label: 'Mark done',
    to: 'completed',
    from: ['todo', 'active', 'paused'],
    note: 'optional',
    variant: 'go',
    icon: 'check',
    title: 'Mark this job done?',
    hint: 'Anything the office should know. Optional.',
    placeholder: 'Beds done, gate left as found',
  },
];

/**
 * May this person change this job's status?
 *
 * Mirrors PUT /api/jobs/:id (jobs.js:511) and canUserReschedule
 * (scheduling.js:73), which agree. `lead_user_id` is what the Field and
 * Dispatch payloads call it; `user_id` is what a raw jobs row calls it. Both
 * are read, because a screen holding either shape must get the same answer.
 */
export function canChangeJobStatus(user, job) {
  if (!user || !job) return false;
  if (user.role === 'admin' || user.role === 'manager') return true;
  const lead = job.lead_user_id !== undefined ? job.lead_user_id : job.user_id;
  return lead != null && String(lead) === String(user.id);
}

/**
 * Why the buttons are not there, or null when they are.
 *
 * A missing control with no explanation reads as a broken screen, and the
 * crew member's next move is to phone the office. Naming the obstacle lets
 * them ask the right person for the right thing.
 */
export function whyStatusRefused(user, job) {
  if (!user || !job) return null;
  if (canChangeJobStatus(user, job)) return null;
  return 'Only the crew lead on this job, or the office, can change its status.';
}

/** The moves available from a job's current status, for this person. */
export function statusActionsFor(job, user) {
  if (!job || !canChangeJobStatus(user, job)) return [];
  const status = job.status || 'todo';
  return JOB_STATUS_ACTIONS.filter((a) => a.to !== status && a.from.includes(status));
}

/** One action by key, or null. */
export function statusAction(key) {
  return JOB_STATUS_ACTIONS.find((a) => a.key === key) || null;
}

/**
 * The PUT body.
 *
 * `status_note` is sent only when there is one — an empty string would be
 * trimmed to '' by the validator and then read as falsy by the handler, which
 * writes the activity line without a note anyway. Sending nothing says the
 * same thing more honestly.
 *
 * Nothing else goes in the body. PUT only writes fields that are present, and
 * a body carrying the rest of the job would stamp an activity line per field
 * and, worse, could rewrite `user_id` — the LEAD pointer payroll, the worker
 * job list and the daily digest all read.
 */
export function jobStatusBody(action, note) {
  const a = typeof action === 'string' ? statusAction(action) : action;
  if (!a) return null;
  const body = { status: a.to };
  const text = typeof note === 'string' ? note.trim() : '';
  if (a.note !== 'none' && text) body.status_note = text.slice(0, 2000);
  return body;
}

/** A sentence naming what is missing, or null when the action may be sent. */
export function validateStatusChange(action, note) {
  const a = typeof action === 'string' ? statusAction(action) : action;
  if (!a) return 'That is not something this job can do.';
  if (a.note === 'required' && !(typeof note === 'string' && note.trim())) {
    return 'Say why you are pausing — the office reads it.';
  }
  return null;
}

/** What to say after it worked. Short; a phone in one hand outdoors. */
export function statusDoneLabel(action) {
  const a = typeof action === 'string' ? statusAction(action) : action;
  if (!a) return null;
  if (a.key === 'start') return 'Started.';
  if (a.key === 'pause') return 'Paused. The office has the reason.';
  return 'Marked done.';
}

/**
 * The sentence for a status change that could not be sent because there is no
 * signal.
 *
 * Deliberately not the offline banner's "saved on this phone" — nothing is
 * saved, and telling a crew member their work is safe when it is not is the
 * failure this whole module is arranged around.
 */
export const OFFLINE_REFUSAL = 'Changing a job\'s status needs a connection — this one is not saved on the phone. Clock in and out still work offline.';
